import type { OptionsListWorkerRequest, OptionsListWorkerResponse, OptionsListWorkerMessage } from './worker';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { replacer, reviver } from '@thinkalpha/common/serialization/serializer.js';
import { inject, injectable } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import { invalidateOptionsListQueries } from 'src/queries/options';
import type { OptionsListService } from 'src/services/OptionsListService';
import type { QueryCacheUserServiceDataSource } from 'src/services/QueryCacheUserService';
import { bridgeQueryCacheUserServiceDataSourceToWorker } from 'src/services/QueryCacheUserService/workerBridge';
import { invalidateOptionsSymbolSearch } from 'src/services/SymbolSearchService/impl/options';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

const TRACE = true;

@injectable()
export class OptionsListServiceImpl implements OptionsListService {
    #disposableStack = new DisposableStack();
    #log: ReactBindings['Logger'];
    #worker: Worker | null = null;
    #pendingRequests = new Map<string, { resolve: (value: any) => void; reject: (reason: any) => void }>();
    #status = signal<TableStatus>('uninitialized');

    get status(): ReadonlySignal<TableStatus> {
        return this.#status as ReadonlySignal<TableStatus>;
    }

    constructor(
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('QueryCacheUserServiceDataSource')
        queryCacheUserServiceDataSource: QueryCacheUserServiceDataSource,
    ) {
        this.#log = logger.getSubLogger({ name: 'service::options-list-service' });

        this.#worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
        this.#worker.addEventListener('message', this.#handleWorkerMessage);
        this.#disposableStack.defer(
            bridgeQueryCacheUserServiceDataSourceToWorker(queryCacheUserServiceDataSource, this.#worker),
        );

        this.#disposableStack.defer(
            store
                .subscribeSelector((state) => state.auth, { leading: true })
                .handle((auth) => this.#sendWorkerRequest({ type: 'updateUser', id: v4(), auth })),
        );
        this.#disposableStack.defer(
            store
                .subscribeSelector((state) => state.ui.superuserAccessEnabledUntil, { leading: true })
                .handle((superUserAccessEnabledUntil) =>
                    this.#sendWorkerRequest({ type: 'updateSuperuser', id: v4(), superUserAccessEnabledUntil }),
                ),
        );
    }

    /**
     * Deserialize a message from the worker
     */
    #deserializeWorkerMessage(data: unknown): OptionsListWorkerResponse | OptionsListWorkerMessage | null {
        if (typeof data !== 'string') {
            if (TRACE) {
                this.#log.trace('Received non-string message from worker', { data });
            }
            return null;
        }

        try {
            return JSON.parse(data, reviver);
        } catch (error) {
            this.#log.error('Error deserializing worker message', { error, data });
            return null;
        }
    }

    #handleWorkerMessage = (event: MessageEvent) => {
        const message = this.#deserializeWorkerMessage(event.data);
        if (!message) return;

        // Messages
        if (message.type === 'connectionStatus') {
            this.#status.value = message.connectionStatus;
            if (TRACE) {
                this.#log.trace('Received connection status', { status: message.connectionStatus });
            }
            return;
        }

        if (message.type === 'trieUpdated') {
            if (TRACE) {
                this.#log.trace('Received trie updated message');
            }
            // Invalidate both the options list queries and the options symbol search queries
            invalidateOptionsListQueries(this.queryClient);
            invalidateOptionsSymbolSearch(this.queryClient);
            return;
        }

        // Responses
        const response = message as OptionsListWorkerResponse;
        const { id, type } = response;

        const pendingRequest = id ? this.#pendingRequests.get(id) : null;
        if (!pendingRequest) {
            if (TRACE) {
                this.#log.trace('Received response for unknown request', { id, type });
            }
            return;
        }

        pendingRequest.resolve(response);
        this.#pendingRequests.delete(id);
    };

    /**
     * Send a strongly-typed request to the worker with serialization
     */
    async #sendWorkerRequest<T extends OptionsListWorkerRequest['type']>(
        request: OptionsListWorkerRequest & { type: T },
    ): Promise<Extract<OptionsListWorkerResponse, { type: T }>> {
        return new Promise<Extract<OptionsListWorkerResponse, { type: T }>>((resolve, reject) => {
            // Register the callback before sending the message
            this.#pendingRequests.set(request.id, { resolve, reject });

            if (TRACE) {
                this.#log.trace('Sending request to worker', { type: request.type, id: request.id });
            }

            // Serialize the request using our custom serializer in one place
            this.#worker!.postMessage(JSON.stringify(request, replacer));
        });
    }

    async fetchOptionsListEntries(search: string): Promise<string[]> {
        try {
            const response = await this.#sendWorkerRequest({
                type: 'search',
                id: v4(),
                prefix: search,
            });

            return response.results;
        } catch (error) {
            this.#log.error('Error searching options', { error, search });
            return [];
        }
    }
}
